import React, { useState, useEffect } from 'react';
import { Button, Input, Tabs, message, Tooltip } from 'antd';
import { CopyOutlined, SendOutlined, CodeOutlined, FormOutlined, PlusOutlined, DeleteOutlined, EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { triggerSequence } from '../../../api/sequence';
import { copyText } from 'helper/text';

const { TextArea } = Input;

const ApiRequestPreview = ({ triggerData, onClose, sequenceDetails, apiKey, isApiKeyLoading }) => {
  const [activeTab, setActiveTab] = useState('body');
  const [requestUrl, setRequestUrl] = useState('');
  const [requestParams, setRequestParams] = useState([{ key: '', value: '' }]);
  const [requestHeaders, setRequestHeaders] = useState([
    { key: 'Content-Type', value: 'application/json' },
    { key: 'X-API-KEY', value: null }
  ]);
  const [requestBody, setRequestBody] = useState('{\n  "mobile_number": "+1234567890"\n}');
  const [isJsonView, setIsJsonView] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);
  const [isValidJson, setIsValidJson] = useState(true);
  const [showApiKey, setShowApiKey] = useState(false);

  // Handle API key and request headers
  useEffect(() => {
    setRequestHeaders(prevHeaders => {
      const newHeaders = prevHeaders.map(header => 
        header.key === 'X-API-KEY' 
          ? { ...header, value: apiKey || '' } 
          : header
      );
      return newHeaders;
    });

    if (apiKey) {
      setError(null); // Clear error when API key is available
    } else if (!isApiKeyLoading) {
      setError('X-API-KEY is not found. Create X-API-KEY in channels section to test API trigger');
    }
  }, [apiKey, isApiKeyLoading]);

  // Handle trigger data and URL setup
  useEffect(() => {
    if (triggerData?.data?.webhook_url) {
      setRequestUrl(triggerData.data.webhook_url);
      
      // Initialize with default parameters from API trigger config
      if (triggerData.data.attribute_mapping) {
        // Create an array of parameters, ensuring mobile_number comes first
        const params = [];
        const otherParams = [];
        // Separate mobile_number from other parameters
        Object.entries(triggerData.data.attribute_mapping).forEach(([key, value]) => {
          if (key === 'mobile_number') {
            params.unshift({ key, value: null }); // ensures mobile_number is first
          } else if (key === 'country_code') {
            // insert after mobile_number (i.e., at index 1)
            params.splice(1, 0, { key, value: null });
          } else {
            otherParams.push({ key, value: null });
          }
        });
        
        // Combine mobile_number (if exists) with other parameters
        const defaultParams = [...params, ...otherParams];
        
        // If no parameters exist, add an empty one
        if (defaultParams.length === 0) {
          defaultParams.push({ key: '', value: '' });
        }
        
        setRequestParams(defaultParams);
        
        // Also update the JSON view
        const initialPayload = {};
        defaultParams.forEach(param => {
          if (param.key) {
            initialPayload[param.key] = param.value;
          }
        });
        setRequestBody(JSON.stringify(initialPayload, null, 2));
      }
    }
  }, [triggerData, apiKey]);

  const validateJson = (jsonString) => {
    try {
      JSON.parse(jsonString);
      return true;
    } catch (e) {
      return false;
    }
  };

  const handleSendRequest = async () => {
    if (!requestUrl) {
      message.error('Request URL is required');
      return;
    }

    if (isJsonView && !validateJson(requestBody)) {
      setError('Invalid JSON format');
      return;
    }
    // Check if mobile_number is provided and not empty
    if (isJsonView) {
      try {
        const parsedBody = JSON.parse(requestBody);
        if (!parsedBody.mobile_number || parsedBody.mobile_number.trim() === '') {
          setError('mobile_number is a required field');
          return;
        }
        if (!parsedBody.country_code || parsedBody.country_code.trim() === '') {
          setError('country_code is a required field');
          return;
        }
      } catch (e) {
        setError('Invalid JSON format');
        return;
      }
    } else {
      // Check in form view
      const mobileParam = requestParams.find(param => param.key === 'mobile_number');
      if (!mobileParam || !mobileParam.value || mobileParam.value.trim() === '') {
        setError('mobile_number is a required field');
        setResponse(null);
        return;
      }
      const countryCodeParam = requestParams.find(param => param.key === 'country_code');
      if (!countryCodeParam || !countryCodeParam.value || countryCodeParam.value.trim() === '') {
        setError('country_code is a required field');
        setResponse(null);
        return;
      }else{
        setError(null);
      }
    }

    try {
      setIsLoading(true);
      setError(null);
      setResponse(null);
      
      const payload = isJsonView 
        ? JSON.parse(requestBody)
        : requestParams.reduce((acc, { key, value }) => {
            if (key) acc[key] = value;
            return acc;
          }, {});

      const headers = requestHeaders.reduce((acc, { key, value }) => {
        if (key) acc[key] = value;
        return acc;
      }, {});

      // Use the triggerSequence API
      const response = await triggerSequence(sequenceDetails?.slug, payload, headers);
      if (response?.status === 200) { 
        setResponse({
          status: response.status,
          data: response.data,
          headers: response.headers
        });
      } else {
        setError(response?.response?.data?.error || 'Failed to send request');
      }
    } catch (err) {
      setError((err.response?.data?.error + ": " + (err.response?.data?.details || "")) || err.response?.data?.message || err.message || 'Failed to send request');
    } finally {
      setIsLoading(false);
    }
  };

  const renderParamsEditor = () => (
    <div className="space-y-3">
      <div className="grid grid-cols-12 gap-2 text-xs text-gray-500 font-[500] mb-1">
        <div className="col-span-5">Key</div>
        <div className="col-span-6">Value</div>
        <div className="col-span-1"></div>
      </div>

      {requestParams.map((param, index) => (
        <div key={index} className="grid grid-cols-12 gap-2 items-center">
          <div className="col-span-5">
            <div className="w-full px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-md text-sm">
              {param.key}
            </div>
          </div>
          <div className="col-span-6">
            <input
              type="text"
              className="w-full px-3 py-1.5 !border !border-gray-300 !rounded-md text-sm outline-none"
              style={{
                border: '1px solid #E5E7EB'
              }}
              placeholder="Enter value"
              onChange={(e) => {
                const newParams = [...requestParams];
                newParams[index].value = e.target.value;
                setRequestParams(newParams);
              }}
            />
          </div>
          <div className="col-span-1 flex justify-center">
            <div className="w-4"></div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="p-4">
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-24">
            <div className="px-3 py-1.5 border border-gray-300 rounded-md bg-gray-50 text-center">
              <span className="font-mono text-sm">POST</span>
            </div>
          </div>
          <div className="flex-1">
            <div className="flex border border-gray-300 rounded-md overflow-hidden">
              <div className="px-3 py-1.5 bg-gray-50 border-r border-gray-300 text-gray-500 text-sm">
                URL
              </div>
              <input
                className="flex-1 px-3 py-1.5 outline-none text-sm"
                value={requestUrl}
                readOnly
                placeholder="Request URL"
              />
            </div>
          </div>
          <button
            className={`px-4 py-1.5 rounded-md flex items-center gap-2 ${
              (isJsonView && !isValidJson) || isLoading || !apiKey
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                : 'bg-[#05805C] text-white hover:bg-[#046a4a]'
            }`}
            onClick={handleSendRequest}
            disabled={(isJsonView && !isValidJson) || isLoading || !apiKey}
            title={!apiKey ? 'X-API-KEY is required to send the request' : ''}
          >
            {isLoading ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Sending...
              </>
            ) : (
              <>
                <SendOutlined className="text-sm" />
                Send
              </>
            )}
          </button>
        </div>

        <div className="border border-gray-200 rounded-md overflow-hidden">
          <div className="flex border-b border-gray-200">
            <button
              className={`px-4 py-2 text-sm font-[500] ${activeTab === 'body' ? 'text-[#05805C] border-b-2 border-[#05805C]' : 'text-gray-600 hover:text-gray-800'}`}
              onClick={() => setActiveTab('body')}
            >
              Body
            </button>
          </div>
          
          <div className="p-4">
            {activeTab === 'body' && renderParamsEditor()}
          </div>
        </div>
      </div>

      {error && (
        <div className="p-4 mb-4 bg-red-50 border border-red-200 rounded">
          <h4 className="font-bold text-red-600 mb-2">Error</h4>
          <pre className="text-red-600 whitespace-pre-wrap text-sm">{error}</pre>
        </div>
      )}

      {response && (
        <div className="mt-6">
          <div className="flex justify-between items-center mb-2">
            <h4 className="font-bold">Response</h4>
            <div className={`text-sm ${response.status === 200 ? 'text-green-600' : 'text-gray-500'} flex items-center gap-2`}>
              {response.status === 200 && (
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              )}
              Status: <span className="font-mono">{response.status} {response.status === 200 ? 'OK' : ''}</span>
            </div>
          </div>
          <div className="relative">
            <pre className="p-4 bg-gray-50 rounded overflow-auto max-h-[10rem] text-sm">
              {JSON.stringify(response.data, null, 2)}
            </pre>
            <Button
              type="text"
              icon={<CopyOutlined />}
              onClick={() => {
                copyText(JSON.stringify(response.data, null, 2));
                message.success('Response copied to clipboard');
              }}
              className="absolute top-2 right-2"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ApiRequestPreview;
